import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Truck, CheckCircle2, Package, Boxes, MapPin, Trophy, UserCheck, Box } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { StatusPill } from "@/components/Pills";
import { useLang } from "@/context/LanguageContext";

// Phase D (Feb 2026): One full-screen page that handles all 7 Management drilldown variants.
// Variants:
//   orders     → all today's orders (sorted by boxes desc)
//   delivered  → only delivered today
//   pending    → only pending today
//   boxes      → product-wise boxes sold
//   districts  → district-wise boxes sold
//   products   → product performance + chart
//   performance → salesmen + managers ranked by boxes (incl. manager direct sales)
const VARIANTS = {
  orders:     { titleKey: "orders",            color: "bg-[#0055FF]", barColor: "#0055FF", icon: Truck,        type: "orders", filter: () => true },
  delivered:  { titleKey: "delivered_today",   color: "bg-[#34C759]", barColor: "#34C759", icon: CheckCircle2, type: "orders", filter: o => o.status === "delivered" },
  pending:    { titleKey: "pending",           color: "bg-[#FF9500]", barColor: "#FF9500", icon: Package,      type: "orders", filter: o => ["pending","assigned","out_for_delivery"].includes(o.status) },
  boxes:      { titleKey: "boxes_sold",        color: "bg-[#5856D6]", barColor: "#5856D6", icon: Boxes,        type: "breakdown", group: "product" },
  districts:  { titleKey: "district_wise_sold",color: "bg-amber-500", barColor: "#F59E0B", icon: MapPin,       type: "breakdown", group: "district" },
  products:   { titleKey: "product_wise_sold", color: "bg-[#0055FF]", barColor: "#0055FF", icon: Box,          type: "breakdown", group: "product" },
  performance:{ titleKey: "best_sales_today",  color: "bg-[#0A1A3F]", barColor: "#0055FF", icon: Trophy,       type: "performance" },
};
const RANK_BADGE = ["🥇", "🥈", "🥉"];
const BAR_COLORS = ["#FFC107", "#9CA3AF", "#CD7F32", "#0055FF", "#5856D6", "#34C759", "#FF9500", "#FF3B30", "#06B6D4"];

export default function ManagementDrilldown() {
  const nav = useNavigate();
  const { variant = "orders" } = useParams();
  const cfg = VARIANTS[variant] || VARIANTS.orders;
  const { t, lang, displayName } = useLang();
  const [orders, setOrders] = useState([]);
  const [breakdown, setBreakdown] = useState({ rows: [] });
  const [perf, setPerf] = useState({ salesmen: [], managers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (cfg.type === "orders") {
      api.get("/orders")
        .then(r => setOrders((r.data || []).filter(cfg.filter)))
        .finally(() => setLoading(false));
    } else if (cfg.type === "breakdown") {
      api.get("/dashboard/units-breakdown", { params: { scope: "today", group_by: cfg.group } })
        .then(r => setBreakdown(r.data || { rows: [] }))
        .finally(() => setLoading(false));
    } else if (cfg.type === "performance") {
      Promise.all([
        api.get("/dashboard/units-breakdown", { params: { scope: "today", group_by: "salesman" } }),
        api.get("/dashboard/management"),
      ]).then(([s, m]) => {
        setPerf({
          salesmen: (s.data?.rows || []).map(r => ({ id: r.id, name: r.name, role: "salesman", units_sold: r.units, total_orders: r.orders })),
          managers: (m.data?.managers || []).map(r => ({ id: r.id, name: r.name, role: "manager", units_sold: r.units_sold || 0, total_orders: r.total_orders || 0, delivered: r.delivered || 0 })),
        });
        setLoading(false);
      });
    }
  // eslint-disable-next-line
  }, [variant]);

  // Combined ranking for performance variant
  const ranking = useMemo(() => {
    if (cfg.type !== "performance") return [];
    const all = [...perf.salesmen.map(s => ({...s})), ...perf.managers.filter(m => (m.units_sold || 0) > 0).map(m => ({...m, name: `${m.name} (Manager)`}))];
    all.sort((a, b) => (b.units_sold || 0) - (a.units_sold || 0));
    return all;
  }, [perf, cfg.type]);

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => (b.total_units || 0) - (a.total_units || 0));
  }, [orders]);

  const totalBoxes = useMemo(() => {
    if (cfg.type === "orders") return sortedOrders.reduce((s, o) => s + (o.total_units || 0), 0);
    if (cfg.type === "breakdown") return (breakdown.rows || []).reduce((s, r) => s + (r.units || 0), 0);
    if (cfg.type === "performance") return ranking.reduce((s, r) => s + (r.units_sold || 0), 0);
    return 0;
  }, [cfg.type, sortedOrders, breakdown, ranking]);

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={t(cfg.titleKey)} onBack={() => nav("/management")} />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Hero summary banner */}
        <div className={`${cfg.color} text-white rounded-3xl p-5 shadow-xl`} data-testid="md-summary">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center">
              <cfg.icon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 font-bold">{t(cfg.titleKey)}</div>
              <div className="heading-font text-3xl font-bold">
                {cfg.type === "orders" ? sortedOrders.length : (cfg.type === "breakdown" ? breakdown.rows?.length || 0 : ranking.length)}
              </div>
              <div className="text-xs opacity-80 mt-0.5 flex items-center gap-1">
                <Boxes className="w-3.5 h-3.5" /> {totalBoxes} {t("boxes")}
              </div>
            </div>
          </div>
        </div>

        {/* Bar chart for breakdown / performance */}
        {(cfg.type === "breakdown" || cfg.type === "performance") && (
          <div className="bg-white rounded-2xl p-5 soft-shadow" data-testid="md-chart">
            <h3 className="heading-font font-bold text-lg mb-3">{t(cfg.titleKey)}</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={cfg.type === "breakdown" ? (breakdown.rows || []).slice(0, 10) : ranking.slice(0, 10).map(r => ({ name: r.name, units: r.units_sold }))}
                  layout="vertical"
                  margin={{ left: 8, right: 16, top: 6, bottom: 6 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: "#86868B" }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12, fill: "#1D1D1F" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} formatter={(v) => [`${v} ${t("boxes")}`, t("boxes_sold")]} />
                  <Bar dataKey="units" radius={[0, 8, 8, 0]}>
                    {(cfg.type === "breakdown" ? breakdown.rows || [] : ranking).slice(0, 10).map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Performance: top-3 podium */}
        {cfg.type === "performance" && ranking.length > 0 && (
          <div className="bg-gradient-to-br from-[#0A1A3F] to-[#0055FF] text-white rounded-3xl p-5 shadow-xl" data-testid="md-podium">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-yellow-300" />
              <div className="heading-font font-bold text-lg">{t("best_sales_today")}</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {ranking.slice(0, 3).map((r, i) => (
                <div key={`${r.role}-${r.id}`} className="bg-white/10 rounded-2xl p-3" data-testid={`md-rank-${i+1}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{RANK_BADGE[i]}</span>
                    <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{r.role === "manager" ? "Manager" : "Salesman"}</div>
                  </div>
                  <div className="font-bold heading-font truncate">{r.name}</div>
                  <div className="heading-font text-2xl font-bold mt-1">{r.units_sold || 0} <span className="text-xs font-medium opacity-70">{t("boxes")}</span></div>
                  <div className="text-[10px] opacity-70 mt-0.5">{r.total_orders || 0} {t("orders")}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#86868B]">{t("loading")}</div>
          ) : cfg.type === "orders" ? (
            sortedOrders.length === 0 ? (
              <div className="p-12 text-center text-[#86868B]" data-testid="md-empty">No orders</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {sortedOrders.map((o, idx) => (
                  <div key={o.id} className="p-4 sm:p-5" data-testid={`md-row-${o.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-[#86868B] tabular-nums">#{idx + 1}</span>
                          <div className="heading-font font-bold text-[#1D1D1F]">{o.shop_name}</div>
                          <StatusPill status={o.status} t={t} />
                        </div>
                        <div className="text-xs text-[#86868B] mt-1 flex flex-wrap gap-2">
                          {o.assigned_salesman_name && <span>→ {o.assigned_salesman_name}</span>}
                          {o.district_name && <span>📍 {o.district_name}</span>}
                          <span>{new Date(o.created_at).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="heading-font text-2xl font-bold text-[#1D1D1F]">{o.total_units || 0}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{t("boxes")}</div>
                      </div>
                    </div>
                    {o.items?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {o.items.slice(0, 5).map((it, i) => (
                          <span key={i} className="pill bg-gray-100 text-gray-700 text-[11px]">{displayName(it)} ×{it.quantity}</span>
                        ))}
                        {o.items.length > 5 && <span className="pill bg-gray-100 text-gray-700 text-[11px]">+{o.items.length - 5}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : cfg.type === "breakdown" ? (
            (breakdown.rows || []).length === 0 ? (
              <div className="p-12 text-center text-[#86868B]" data-testid="md-empty">No data</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50/60">
                  <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                    <th className="px-4 py-3">{t("rank")}</th>
                    <th className="px-4 py-3">{t("name")}</th>
                    <th className="px-4 py-3">{t("orders")}</th>
                    <th className="px-4 py-3">{t("boxes_sold")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(breakdown.rows || []).map((r, i) => (
                    <tr key={r.id || r._id || i} className="hover:bg-gray-50/40" data-testid={`md-bd-row-${i}`}>
                      <td className="px-4 py-3 font-bold text-[#86868B]">{i < 3 ? <span className="text-xl">{RANK_BADGE[i]}</span> : `#${i+1}`}</td>
                      <td className="px-4 py-3 font-semibold heading-font">{r.name}</td>
                      <td className="px-4 py-3">{r.orders || 0}</td>
                      <td className="px-4 py-3 font-bold text-[#1D1D1F]">{r.units || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            // performance table
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{t("rank")}</th>
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">{t("orders")}</th>
                  <th className="px-4 py-3">{t("boxes_sold")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ranking.map((r, i) => (
                  <tr key={`${r.role}-${r.id}`} className="hover:bg-gray-50/40" data-testid={`md-perf-row-${i}`}>
                    <td className="px-4 py-3 font-bold text-[#86868B]">{i < 3 ? <span className="text-xl">{RANK_BADGE[i]}</span> : `#${i+1}`}</td>
                    <td className="px-4 py-3 font-semibold heading-font">{r.name}</td>
                    <td className="px-4 py-3">
                      <span className={`pill ${r.role === "manager" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>
                        {r.role === "manager" ? "Manager" : "Salesman"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{r.total_orders || 0}</td>
                    <td className="px-4 py-3 font-bold text-[#1D1D1F]">{r.units_sold || 0}</td>
                  </tr>
                ))}
                {ranking.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-[#86868B]">No data</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
